import type p5 from 'p5';
import type { PoseData, VisualConfig, PoseLandmark } from '../../../types';
import type { MotionData } from '../../../utils/MotionTracker';
import { generateTorsoCircles } from './growing-circles/TorsoCircleGenerator';
import type { CircleGenConfig, Circle, LimbSegment } from './growing-circles/CircleTypes';
import type { IArtisticRenderer } from './IArtisticRenderer';

/**
 * Growing Circles Artistic Visualization.
 * 
 * Displays animated circles along body limbs that grow and vanish continuously.
 * Circle size is responsive to limb motion intensity, creating a dynamic, organic feel.
 * 
 * Features:
 * - Circles placed evenly along each limb segment
 * - Each circle grows from small to max size then vanishes
 * - Growth timing varies per circle for wave effect
 * - Max size scales with motion intensity (2x factor between low and high motion)
 * - Uses current color palette from visual config
 * 
 * Validates Requirements 3.5.1.1, 3.5.2
 */
export class GrowingCirclesRenderer implements IArtisticRenderer {
  private circles: Map<string, Circle[]> = new Map();
  private headCircles: Circle[] = [];
  private neckCircles: Circle[] = [];
  private torsoCircles: Circle[] = [];
  private readonly baseCircleSize = 40; // Base max size in pixels (low motion) - doubled from 20
  private readonly minCircles = 2;
  private readonly maxCircles = 20; // Increased from 8 for higher density
  private readonly spacingFactor = 0.4; // Reduced from 0.75 for overlapping circles
  private readonly spacingRandomness = 0.1; // ±10% randomness
  private readonly minGrowthDuration = 0.5; // seconds
  private readonly maxGrowthDuration = 1.5; // seconds
  private readonly growthRateVariation = 0.2; // ±20% variation
  private readonly startSizeFactor = 0.1; // Start at 10% of max size
  private readonly circleAlpha = 0.8; // Transparency
  private readonly gridRandomness = 0.2; // ±20% randomness for grid positions
  private readonly torsoGridRandomness = 0.15; // ±15% randomness for torso grid positions
  
  private lastUpdateTime = 0;
  
  /** Config bundle passed to extracted circle generators. */
  private readonly circleGenConfig: CircleGenConfig = {
    baseCircleSize: this.baseCircleSize,
    minGrowthDuration: this.minGrowthDuration,
    maxGrowthDuration: this.maxGrowthDuration,
    growthRateVariation: this.growthRateVariation,
    startSizeFactor: this.startSizeFactor,
    torsoGridRandomness: this.torsoGridRandomness,
  };
  
  constructor() {
    this.reset();
  }
  
  /**
   * Render the growing circles visualization.
   */
  render(p: p5, pose: PoseData, config: VisualConfig, motionData: MotionData): void {
    if (!pose.detected) {
      return;
    }
    
    // Calculate time delta
    const currentTime = performance.now() / 1000; // Convert to seconds
    if (this.lastUpdateTime === 0) {
      this.lastUpdateTime = currentTime;
      this.initializeCircles(pose, motionData);
      // Don't return - continue to render the initialized circles
    }
    
    const deltaTime = currentTime - this.lastUpdateTime;
    this.lastUpdateTime = currentTime;
    
    // Update and render circles for each limb
    const limbs = this.getLimbSegments(pose);
    
    for (const limb of limbs) {
      // Skip limb if landmarks have low visibility
      if (limb.start.visibility < 0.5 || limb.end.visibility < 0.5) {
        continue;
      }
      
      // Get or initialize circles for this limb
      if (!this.circles.has(limb.name)) {
        this.initializeCirclesForLimb(limb, motionData);
      }
      
      const limbCircles = this.circles.get(limb.name);
      if (!limbCircles) {
        continue;
      }
      
      // Update circle positions and sizes (skip on first frame when deltaTime is 0)
      if (deltaTime > 0) {
        this.updateCircles(limbCircles, limb, deltaTime, motionData);
      }
      
      // Render circles
      this.renderCircles(p, limbCircles, config);
    }
    
    // Handle head circles
    const { nose, leftEar, rightEar } = pose.landmarks;
    if (nose.visibility >= 0.5 && leftEar.visibility >= 0.5 && rightEar.visibility >= 0.5) {
      // Initialize head circles if needed
      if (this.headCircles.length === 0) {
        this.headCircles = this.generateHeadEllipseCircles(nose, leftEar, rightEar);
      }
      
      // Update head circles (skip on first frame when deltaTime is 0)
      if (deltaTime > 0 && this.headCircles.length > 0) {
        const headMotionIntensity = motionData.head?.intensity ?? 0;
        this.updateHeadCircles(this.headCircles, nose, leftEar, rightEar, deltaTime, headMotionIntensity);
      }
      
      // Render head circles
      this.renderCircles(p, this.headCircles, config);
    } else {
      // Clear head circles if landmarks are not visible
      this.headCircles = [];
    }
    
    // Handle neck circles
    const { leftShoulder, rightShoulder } = pose.landmarks;
    if (nose.visibility >= 0.5 && leftShoulder.visibility >= 0.5 && rightShoulder.visibility >= 0.5) {
      // Calculate shoulder midpoint
      const shoulderMidpoint = {
        x: (leftShoulder.x + rightShoulder.x) / 2,
        y: (leftShoulder.y + rightShoulder.y) / 2,
        visibility: Math.min(leftShoulder.visibility, rightShoulder.visibility)
      };
      
      // Initialize neck circles if needed
      if (this.neckCircles.length === 0 && leftEar.visibility >= 0.5 && rightEar.visibility >= 0.5) {
        // Calculate head ellipse width for cylinder radius
        const earDx = rightEar.x - leftEar.x;
        const earDy = rightEar.y - leftEar.y;
        const headEllipseWidth = Math.sqrt(earDx * earDx + earDy * earDy);
        
        this.neckCircles = this.generateNeckCylinderCircles(nose, shoulderMidpoint, headEllipseWidth);
      }
      
      // Update neck circles (skip on first frame when deltaTime is 0)
      if (deltaTime > 0 && this.neckCircles.length > 0) {
        const neckMotionIntensity = motionData.neck?.intensity ?? 0;
        this.updateNeckCircles(this.neckCircles, nose, shoulderMidpoint, deltaTime, neckMotionIntensity);
      }
      
      // Render neck circles
      this.renderCircles(p, this.neckCircles, config);
    } else {
      // Clear neck circles if landmarks are not visible
      this.neckCircles = [];
    }
    
    // Handle torso circles
    if (leftShoulder.visibility >= 0.5 && rightShoulder.visibility >= 0.5) {
      // Initialize torso circles if needed
      if (this.torsoCircles.length === 0) {
        this.torsoCircles = this.generateTorsoPolygonCircles(pose.landmarks);
      }
      
      // Update torso circles (skip on first frame when deltaTime is 0)
      if (deltaTime > 0 && this.torsoCircles.length > 0) {
        const torsoMotionIntensity = motionData.torso?.intensity ?? 0;
        this.updateTorsoCircles(this.torsoCircles, pose.landmarks, deltaTime, torsoMotionIntensity);
      }
      
      // Render torso circles
      this.renderCircles(p, this.torsoCircles, config);
    } else {
      // Clear torso circles if shoulders are not visible
      this.torsoCircles = [];
    }
  }
  
  /**
   * Reset all circle state.
   */
  reset(): void {
    this.circles.clear();
    this.headCircles = [];
    this.neckCircles = [];
    this.torsoCircles = [];
    this.lastUpdateTime = 0;
  }
  
  /**
   * Get all limb segments from pose data.
   * Includes arms, legs, and torso segments.
   * Note: Neck is handled separately with cylinder circle generation.
   */
  private getLimbSegments(pose: PoseData): LimbSegment[] {
    const { landmarks } = pose;
    
    return [
      // Arms
      { start: landmarks.leftShoulder, end: landmarks.leftElbow, name: 'leftUpperArm' },
      { start: landmarks.leftElbow, end: landmarks.leftWrist, name: 'leftForearm' },
      { start: landmarks.rightShoulder, end: landmarks.rightElbow, name: 'rightUpperArm' },
      { start: landmarks.rightElbow, end: landmarks.rightWrist, name: 'rightForearm' },
      
      // Legs
      { start: landmarks.leftHip, end: landmarks.leftKnee, name: 'leftThigh' },
      { start: landmarks.leftKnee, end: landmarks.leftAnkle, name: 'leftShin' },
      { start: landmarks.rightHip, end: landmarks.rightKnee, name: 'rightThigh' },
      { start: landmarks.rightKnee, end: landmarks.rightAnkle, name: 'rightShin' },
      
      // Torso segments
      { start: landmarks.leftShoulder, end: landmarks.rightShoulder, name: 'shoulders' },
      { start: landmarks.leftHip, end: landmarks.rightHip, name: 'hips' },
      { start: landmarks.leftShoulder, end: landmarks.leftHip, name: 'leftTorso' },
      { start: landmarks.rightShoulder, end: landmarks.rightHip, name: 'rightTorso' },
    ];
  }
  
  /**
   * Initialize circles for all limbs.
   */
  private initializeCircles(pose: PoseData, motionData: MotionData): void {
    const limbs = this.getLimbSegments(pose);
    
    for (const limb of limbs) {
      if (limb.start.visibility >= 0.5 && limb.end.visibility >= 0.5) {
        this.initializeCirclesForLimb(limb, motionData);
      }
    }
  }
  
  /**
   * Initialize circles for a single limb segment.
   * Calculates number of circles based on limb length and places them evenly along the limb.
   * 
   * Validates Requirements 3.5.1.2, 3.5.1.3, 3.5.1.4
   */
  private initializeCirclesForLimb(limb: LimbSegment, _motionData: MotionData): void {
    // Calculate limb length in normalized coordinates
    const dx = limb.end.x - limb.start.x;
    const dy = limb.end.y - limb.start.y;
    const limbLength = Math.sqrt(dx * dx + dy * dy);
    
    // Convert to approximate pixel length (using reference width of 1920)
    const referenceWidth = 1920;
    const limbLengthPixels = limbLength * referenceWidth;
    
    // Calculate base spacing (0.75x base circle size)
    const baseSpacing = this.baseCircleSize * this.spacingFactor;
    
    // Calculate number of circles based on limb length
    // More circles for longer limbs, rather than increasing spacing
    let numCircles = Math.floor(limbLengthPixels / baseSpacing);
    
    // Clamp to min/max range
    numCircles = Math.max(this.minCircles, Math.min(this.maxCircles, numCircles));
    
    // Create circles along the limb
    const circles: Circle[] = [];
    
    for (let i = 0; i < numCircles; i++) {
      // Calculate position along limb with slight randomness
      const t = i / (numCircles - 1 || 1); // Interpolation factor [0, 1]
      
      // Add ±10% randomness to spacing
      const randomOffset = (Math.random() - 0.5) * 2 * this.spacingRandomness;
      const tWithRandomness = Math.max(0, Math.min(1, t + randomOffset));
      
      // Interpolate position between start and end
      const position = {
        x: limb.start.x + (limb.end.x - limb.start.x) * tWithRandomness,
        y: limb.start.y + (limb.end.y - limb.start.y) * tWithRandomness
      };
      
      // Random growth duration between 0.5s and 1.5s
      const growthDuration = this.minGrowthDuration + 
        Math.random() * (this.maxGrowthDuration - this.minGrowthDuration);
      
      // Calculate growth rate with ±20% variation
      const baseGrowthRate = this.baseCircleSize / growthDuration;
      const growthRateVariation = (Math.random() - 0.5) * 2 * this.growthRateVariation;
      const growthRate = baseGrowthRate * (1 + growthRateVariation);
      
      // Stagger start times along limb for wave effect
      const staggeredAge = -(i / numCircles) * growthDuration;
      
      circles.push({
        position,
        currentSize: this.baseCircleSize * this.startSizeFactor,
        maxSize: this.baseCircleSize, // Will be updated based on motion
        growthRate,
        age: staggeredAge,
        growthDuration
      });
    }
    
    this.circles.set(limb.name, circles);
  }
  
  /**
   * Generate circles within head ellipse bounds.
   * Creates a randomized grid of circles that fill the head ellipse area.
   * 
   * Validates Requirements 3.5.1.2.1, 3.5.1.2.2, 3.5.1.2.3, 3.5.1.2.4, 3.5.1.2.5
   * 
   * @param nose Nose landmark (ellipse center)
   * @param leftEar Left ear landmark
   * @param rightEar Right ear landmark
   * @returns Array of circles positioned within the head ellipse
   */
  private generateHeadEllipseCircles(
    nose: PoseLandmark,
    leftEar: PoseLandmark,
    rightEar: PoseLandmark
  ): Circle[] {
    // Skip if landmarks have low visibility
    if (nose.visibility < 0.5 || leftEar.visibility < 0.5 || rightEar.visibility < 0.5) {
      return [];
    }
    
    // Calculate ellipse dimensions
    // Width = ear-to-ear distance
    const earDx = rightEar.x - leftEar.x;
    const earDy = rightEar.y - leftEar.y;
    const ellipseWidth = Math.sqrt(earDx * earDx + earDy * earDy);
    
    // Height = 1.3x width for natural head proportions
    const ellipseHeight = ellipseWidth * 1.3;
    
    // Ellipse center at nose position
    const centerX = nose.x;
    const centerY = nose.y;
    
    // Semi-axes for ellipse equation
    const a = ellipseWidth / 2; // Semi-major axis (horizontal)
    const b = ellipseHeight / 2; // Semi-minor axis (vertical)
    
    // Calculate ellipse area (approximate)
    const ellipseArea = Math.PI * a * b;
    
    // Convert to pixel area (using reference width)
    const referenceWidth = 1920;
    const ellipseAreaPixels = ellipseArea * referenceWidth * referenceWidth;
    
    // Calculate target number of circles based on density
    // Target density: similar to limb density (~15-25 circles for average head)
    const circleAreaPixels = Math.PI * (this.baseCircleSize / 2) * (this.baseCircleSize / 2);
    const targetCircles = Math.floor(ellipseAreaPixels / (circleAreaPixels * 2)); // Divide by 2 for spacing
    
    // Clamp to reasonable range (15-25 circles)
    const numCircles = Math.max(15, Math.min(25, targetCircles));
    
    // Generate randomized grid positions
    const circles: Circle[] = [];
    
    // Use elliptical grid generation for proper distribution
    // Generate circles in concentric elliptical rings
    const numRings = Math.ceil(Math.sqrt(numCircles / Math.PI)); // Approximate number of rings
    let circlesGenerated = 0;
    
    for (let ring = 0; ring < numRings && circlesGenerated < numCircles; ring++) {
      // Calculate radius for this ring (0 to 1)
      const ringRadius = (ring + 0.5) / numRings;
      
      // Calculate circumference of this elliptical ring
      // Approximation: C ≈ π * (a + b) for ellipse with semi-axes a, b
      const ringCircumference = Math.PI * (ringRadius + ringRadius * 1.3);
      
      // Number of circles in this ring based on circumference
      const circlesInRing = Math.max(1, Math.round(ringCircumference * numRings * 0.8));
      
      for (let i = 0; i < circlesInRing && circlesGenerated < numCircles; i++) {
        // Angle around the ellipse
        const angle = (i / circlesInRing) * 2 * Math.PI;
        
        // Add angular randomness (±10%)
        const angleRandomness = (Math.random() - 0.5) * 2 * 0.1 * (2 * Math.PI / circlesInRing);
        const randomAngle = angle + angleRandomness;
        
        // Add radial randomness (±20% of ring spacing)
        const radialRandomness = (Math.random() - 0.5) * 2 * this.gridRandomness * (1 / numRings);
        const randomRadius = Math.max(0, Math.min(1, ringRadius + radialRandomness));
        
        // Calculate position on ellipse using parametric form
        // Parametric ellipse: x = r*cos(θ), y = r*sin(θ) in normalized space
        // The ellipse has aspect ratio 1:1.3 (width:height)
        // We generate in a unit circle space, then scale y by 1.3 to create the ellipse
        const x = randomRadius * Math.cos(randomAngle);
        const y = randomRadius * Math.sin(randomAngle);
        
        // Verify position is within unit circle (should always be true)
        const circleTest = (x * x) + (y * y);
        
        if (circleTest <= 1.0) {
          // Convert from normalized circle space to world coordinates
          // Scale x by horizontal semi-axis (a) and y by vertical semi-axis (b = 1.3*a)
          const worldX = centerX + x * a;
          const worldY = centerY + y * b;
          
          // Random growth duration
          const growthDuration = this.minGrowthDuration + 
            Math.random() * (this.maxGrowthDuration - this.minGrowthDuration);
          
          // Calculate growth rate with variation
          const baseGrowthRate = this.baseCircleSize / growthDuration;
          const growthRateVariation = (Math.random() - 0.5) * 2 * this.growthRateVariation;
          const growthRate = baseGrowthRate * (1 + growthRateVariation);
          
          // Stagger start times for wave effect
          const staggeredAge = -(circlesGenerated / numCircles) * growthDuration;
          
          circles.push({
            position: { x: worldX, y: worldY },
            currentSize: this.baseCircleSize * this.startSizeFactor,
            maxSize: this.baseCircleSize,
            growthRate,
            age: staggeredAge,
            growthDuration,
            // Store relative position in normalized ellipse space [-1, 1]
            relativePosition: { x, y }
          });
          
          circlesGenerated++;
        }
      }
    }
    
    return circles;
  }
  
  /**
   * Generate circles within neck cylindrical region.
   * Creates a randomized grid of circles along the neck segment within a cylindrical boundary.
   * 
   * Validates Requirements 3.5.1.3.1, 3.5.1.3.2, 3.5.1.3.3, 3.5.1.3.4
   * 
   * @param nose Nose landmark (neck start)
   * @param shoulderMidpoint Shoulder midpoint (neck end)
   * @param headEllipseWidth Width of head ellipse (for cylinder radius calculation)
   * @returns Array of circles positioned within the neck cylinder
   */
  private generateNeckCylinderCircles(
    nose: PoseLandmark,
    shoulderMidpoint: { x: number; y: number; visibility: number },
    headEllipseWidth: number
  ): Circle[] {
    // Skip if landmarks have low visibility
    if (nose.visibility < 0.5 || shoulderMidpoint.visibility < 0.5) {
      return [];
    }
    
    // Calculate neck segment
    const neckStart = { x: nose.x, y: nose.y };
    const neckEnd = shoulderMidpoint;
    
    // Calculate neck length
    const neckDx = neckEnd.x - neckStart.x;
    const neckDy = neckEnd.y - neckStart.y;
    const neckLength = Math.sqrt(neckDx * neckDx + neckDy * neckDy);
    
    // Cylinder radius: 0.15x head ellipse width (narrower for more natural appearance)
    // Note: Requirements specify 0.5x but that's too wide visually
    const cylinderRadius = headEllipseWidth * 0.15;
    
    // Convert to pixel length (using reference width)
    const referenceWidth = 1920;
    const neckLengthPixels = neckLength * referenceWidth;
    const cylinderRadiusPixels = cylinderRadius * referenceWidth;
    
    // Calculate cylinder area (approximate as rectangle)
    const cylinderAreaPixels = neckLengthPixels * (cylinderRadiusPixels * 2);
    
    // Calculate target number of circles based on density
    // Target density: ~12-18 circles for average neck length (increased from 8-12)
    const circleAreaPixels = Math.PI * (this.baseCircleSize / 2) * (this.baseCircleSize / 2);
    const targetCircles = Math.floor(cylinderAreaPixels / (circleAreaPixels * 1.5)); // Divide by 1.5 for denser spacing
    
    // Clamp to reasonable range (12-18 circles)
    const numCircles = Math.max(12, Math.min(18, targetCircles));
    
    // Generate randomized grid positions along neck
    const circles: Circle[] = [];
    
    // Calculate perpendicular direction to neck (for offsets)
    const neckDirection = { x: neckDx / neckLength, y: neckDy / neckLength };
    const perpDirection = { x: -neckDirection.y, y: neckDirection.x };
    
    // Create grid along neck with perpendicular offsets
    const gridPointsAlongNeck = Math.ceil(Math.sqrt(numCircles));
    const gridPointsPerpendicular = Math.ceil(numCircles / gridPointsAlongNeck);
    
    for (let i = 0; i < gridPointsAlongNeck; i++) {
      for (let j = 0; j < gridPointsPerpendicular; j++) {
        // Position along neck [0, 1]
        const t = i / (gridPointsAlongNeck - 1 || 1);
        
        // Perpendicular offset [-1, 1] (normalized to cylinder radius)
        const perpOffset = (j / (gridPointsPerpendicular - 1 || 1)) * 2 - 1;
        
        // Add ±20% randomness
        const randomT = t + (Math.random() - 0.5) * 2 * this.gridRandomness;
        const randomPerpOffset = perpOffset + (Math.random() - 0.5) * 2 * this.gridRandomness;
        
        // Clamp to valid ranges
        const clampedT = Math.max(0, Math.min(1, randomT));
        const clampedPerpOffset = Math.max(-1, Math.min(1, randomPerpOffset));
        
        // Check if position is within cylinder radius
        if (Math.abs(clampedPerpOffset) <= 1.0) {
          // Calculate world position
          const baseX = neckStart.x + neckDx * clampedT;
          const baseY = neckStart.y + neckDy * clampedT;
          
          const worldX = baseX + perpDirection.x * cylinderRadius * clampedPerpOffset;
          const worldY = baseY + perpDirection.y * cylinderRadius * clampedPerpOffset;
          
          // Random growth duration
          const growthDuration = this.minGrowthDuration + 
            Math.random() * (this.maxGrowthDuration - this.minGrowthDuration);
          
          // Calculate growth rate with variation
          const baseGrowthRate = this.baseCircleSize / growthDuration;
          const growthRateVariation = (Math.random() - 0.5) * 2 * this.growthRateVariation;
          const growthRate = baseGrowthRate * (1 + growthRateVariation);
          
          // Stagger start times for wave effect
          const staggeredAge = -(circles.length / numCircles) * growthDuration;
          
          circles.push({
            position: { x: worldX, y: worldY },
            currentSize: this.baseCircleSize * this.startSizeFactor,
            maxSize: this.baseCircleSize,
            growthRate,
            age: staggeredAge,
            growthDuration,
            // Store relative position along neck (t) and perpendicular offset
            relativePosition: { x: clampedT, y: clampedPerpOffset }
          });
          
          // Stop when we have enough circles
          if (circles.length >= numCircles) {
            return circles;
          }
        }
      }
    }
    
    return circles;
  }
  
  /**
   * Generate circles within torso polygon bounds.
   * Creates a randomized grid of circles that fill the torso polygon area.
   * 
   * Validates Requirements 3.3.4.1, 3.3.4.2, 3.3.4.3, 3.3.4.4, 3.3.4.5, 3.3.4.6
   * 
   * @param landmarks Pose landmarks
   * @returns Array of circles positioned within the torso polygon
   */
  private generateTorsoPolygonCircles(landmarks: PoseData['landmarks']): Circle[] {
    return generateTorsoCircles(landmarks, this.circleGenConfig);
  }
  
  /**
   * Update circles for a limb segment.
   * Handles circle growth, death, and rebirth lifecycle.
   * Updates circle positions to follow limb movement.
   * 
   * Validates Requirements 3.5.1.5, 3.5.1.6, 3.5.1.7, 3.5.1.8
   */
  private updateCircles(
    circles: Circle[],
    limb: LimbSegment,
    deltaTime: number,
    motionData: MotionData
  ): void {
    // Get motion intensity for this limb to update max sizes
    const limbMotion = this.getLimbMotionIntensity(limb.name, motionData);
    
    for (let i = 0; i < circles.length; i++) {
      const circle = circles[i];
      if (!circle) {
        continue;
      }
      
      // Update age
      circle.age += deltaTime;
      
      // Update max size based on motion intensity
      // Base size (low motion): 40 pixels
      // High motion size: 120 pixels (3x factor)
      // Formula: maxSize = baseSize * (1 + 2 * intensity)
      const motionResponsiveMaxSize = this.baseCircleSize * (1 + 2 * limbMotion);
      
      // Smooth max size changes to avoid jarring jumps
      const maxSizeSmoothingFactor = 0.1;
      circle.maxSize = circle.maxSize + (motionResponsiveMaxSize - circle.maxSize) * maxSizeSmoothingFactor;
      
      // Update position to follow limb movement
      // Recalculate position along limb based on circle's index
      const t = i / (circles.length - 1 || 1);
      circle.position = {
        x: limb.start.x + (limb.end.x - limb.start.x) * t,
        y: limb.start.y + (limb.end.y - limb.start.y) * t
      };
      
      // Handle lifecycle
      if (circle.age < 0) {
        // Circle hasn't started yet (staggered start)
        circle.currentSize = circle.maxSize * this.startSizeFactor;
      } else if (circle.age < circle.growthDuration) {
        // Circle is growing
        // Linear growth from start size to max size
        const growthProgress = circle.age / circle.growthDuration;
        const startSize = circle.maxSize * this.startSizeFactor;
        circle.currentSize = startSize + (circle.maxSize - startSize) * growthProgress;
      } else {
        // Circle has reached max size - vanish and rebirth
        // Reset circle with new random timing
        circle.age = 0;
        circle.currentSize = circle.maxSize * this.startSizeFactor;
        
        // New random growth duration
        circle.growthDuration = this.minGrowthDuration + 
          Math.random() * (this.maxGrowthDuration - this.minGrowthDuration);
        
        // New growth rate with variation
        const baseGrowthRate = circle.maxSize / circle.growthDuration;
        const growthRateVariation = (Math.random() - 0.5) * 2 * this.growthRateVariation;
        circle.growthRate = baseGrowthRate * (1 + growthRateVariation);
      }
    }
  }
  
  /**
   * Update head circles with lifecycle and motion-responsive sizing.
   * Applies same lifecycle as limb circles: birth, growth, death, rebirth.
   * 
   * Validates Requirements 3.5.1.2.6, 3.5.1.2.8
   * 
   * @param circles Array of head circles to update
   * @param nose Nose landmark (head center)
   * @param leftEar Left ear landmark
   * @param rightEar Right ear landmark
   * @param deltaTime Time since last update in seconds
   * @param headMotionIntensity Motion intensity for head [0, 1]
   */
  private updateHeadCircles(
    circles: Circle[],
    nose: PoseLandmark,
    leftEar: PoseLandmark,
    rightEar: PoseLandmark,
    deltaTime: number,
    headMotionIntensity: number
  ): void {
    // Calculate ellipse dimensions for position updates
    const earDx = rightEar.x - leftEar.x;
    const earDy = rightEar.y - leftEar.y;
    const ellipseWidth = Math.sqrt(earDx * earDx + earDy * earDy);
    const ellipseHeight = ellipseWidth * 1.3;
    
    const centerX = nose.x;
    const centerY = nose.y;
    const a = ellipseWidth / 2;
    const b = ellipseHeight / 2;
    
    for (const circle of circles) {
      // Update age
      circle.age += deltaTime;
      
      // Update max size based on motion intensity
      // Formula: maxSize = baseSize * (1 + 2 * intensity)
      const motionResponsiveMaxSize = this.baseCircleSize * (1 + 2 * headMotionIntensity);
      
      // Smooth max size changes
      const maxSizeSmoothingFactor = 0.1;
      circle.maxSize = circle.maxSize + (motionResponsiveMaxSize - circle.maxSize) * maxSizeSmoothingFactor;
      
      // Update position to follow head movement using stored relative position
      if (circle.relativePosition) {
        // Use the stored relative position in normalized ellipse space
        circle.position = {
          x: centerX + circle.relativePosition.x * a,
          y: centerY + circle.relativePosition.y * b
        };
      }
      
      // Handle lifecycle (same as limb circles)
      if (circle.age < 0) {
        circle.currentSize = circle.maxSize * this.startSizeFactor;
      } else if (circle.age < circle.growthDuration) {
        const growthProgress = circle.age / circle.growthDuration;
        const startSize = circle.maxSize * this.startSizeFactor;
        circle.currentSize = startSize + (circle.maxSize - startSize) * growthProgress;
      } else {
        // Rebirth
        circle.age = 0;
        circle.currentSize = circle.maxSize * this.startSizeFactor;
        
        circle.growthDuration = this.minGrowthDuration + 
          Math.random() * (this.maxGrowthDuration - this.minGrowthDuration);
        
        const baseGrowthRate = circle.maxSize / circle.growthDuration;
        const growthRateVariation = (Math.random() - 0.5) * 2 * this.growthRateVariation;
        circle.growthRate = baseGrowthRate * (1 + growthRateVariation);
      }
    }
  }
  
  /**
   * Update neck circles with lifecycle and motion-responsive sizing.
   * Applies same lifecycle as limb circles: birth, growth, death, rebirth.
   * 
   * Validates Requirements 3.5.1.3.5, 3.5.1.3.7
   * 
   * @param circles Array of neck circles to update
   * @param nose Nose landmark (neck start)
   * @param shoulderMidpoint Shoulder midpoint (neck end)
   * @param deltaTime Time since last update in seconds
   * @param neckMotionIntensity Motion intensity for neck [0, 1]
   */
  private updateNeckCircles(
    circles: Circle[],
    nose: PoseLandmark,
    shoulderMidpoint: { x: number; y: number },
    deltaTime: number,
    neckMotionIntensity: number
  ): void {
    // Calculate neck segment for position updates
    const neckStart = { x: nose.x, y: nose.y };
    const neckEnd = shoulderMidpoint;
    
    const neckDx = neckEnd.x - neckStart.x;
    const neckDy = neckEnd.y - neckStart.y;
    const neckLength = Math.sqrt(neckDx * neckDx + neckDy * neckDy);
    
    const neckDirection = { x: neckDx / neckLength, y: neckDy / neckLength };
    const perpDirection = { x: -neckDirection.y, y: neckDirection.x };
    
    // Calculate cylinder radius for this frame
    // We don't have ear data here, so approximate based on neck length
    const headEllipseWidth = neckLength * 0.5; // Approximate
    const cylinderRadius = headEllipseWidth * 0.15; // Match the generation radius
    
    for (const circle of circles) {
      // Update age
      circle.age += deltaTime;
      
      // Update max size based on motion intensity
      // Formula: maxSize = baseSize * (1 + 2 * intensity)
      const motionResponsiveMaxSize = this.baseCircleSize * (1 + 2 * neckMotionIntensity);
      
      // Smooth max size changes
      const maxSizeSmoothingFactor = 0.1;
      circle.maxSize = circle.maxSize + (motionResponsiveMaxSize - circle.maxSize) * maxSizeSmoothingFactor;
      
      // Update position to follow neck movement using stored relative position
      if (circle.relativePosition) {
        const t = circle.relativePosition.x; // Position along neck [0, 1]
        const perpOffset = circle.relativePosition.y; // Perpendicular offset [-1, 1]
        
        // Calculate base position along neck
        const baseX = neckStart.x + neckDx * t;
        const baseY = neckStart.y + neckDy * t;
        
        // Apply perpendicular offset
        circle.position = {
          x: baseX + perpDirection.x * cylinderRadius * perpOffset,
          y: baseY + perpDirection.y * cylinderRadius * perpOffset
        };
      }
      
      // Handle lifecycle (same as limb circles)
      if (circle.age < 0) {
        circle.currentSize = circle.maxSize * this.startSizeFactor;
      } else if (circle.age < circle.growthDuration) {
        const growthProgress = circle.age / circle.growthDuration;
        const startSize = circle.maxSize * this.startSizeFactor;
        circle.currentSize = startSize + (circle.maxSize - startSize) * growthProgress;
      } else {
        // Rebirth
        circle.age = 0;
        circle.currentSize = circle.maxSize * this.startSizeFactor;
        
        circle.growthDuration = this.minGrowthDuration + 
          Math.random() * (this.maxGrowthDuration - this.minGrowthDuration);
        
        const baseGrowthRate = circle.maxSize / circle.growthDuration;
        const growthRateVariation = (Math.random() - 0.5) * 2 * this.growthRateVariation;
        circle.growthRate = baseGrowthRate * (1 + growthRateVariation);
      }
    }
  }
  
  /**
   * Update torso circles with lifecycle and motion-responsive sizing.
   * Applies same lifecycle as limb circles: birth, growth, death, rebirth.
   * 
   * Validates Requirements 3.3.4.7, 3.3.4.8, 3.3.4.9
   * 
   * @param circles Array of torso circles to update
   * @param landmarks Pose landmarks
   * @param deltaTime Time since last update in seconds
   * @param torsoMotionIntensity Motion intensity for torso [0, 1]
   */
  private updateTorsoCircles(
    circles: Circle[],
    landmarks: PoseData['landmarks'],
    deltaTime: number,
    torsoMotionIntensity: number
  ): void {
    // Check if shoulders are visible (required for torso)
    const leftShoulderVisible = landmarks.leftShoulder.visibility >= 0.5;
    const rightShoulderVisible = landmarks.rightShoulder.visibility >= 0.5;
    
    if (!leftShoulderVisible || !rightShoulderVisible) {
      return;
    }
    
    // Calculate torso polygon vertices for position updates
    const leftShoulder = landmarks.leftShoulder;
    const rightShoulder = landmarks.rightShoulder;
    
    const shoulderMidX = (leftShoulder.x + rightShoulder.x) / 2;
    const shoulderMidY = (leftShoulder.y + rightShoulder.y) / 2;
    const shoulderDx = rightShoulder.x - leftShoulder.x;
    const shoulderDy = rightShoulder.y - leftShoulder.y;
    const shoulderWidth = Math.sqrt(shoulderDx * shoulderDx + shoulderDy * shoulderDy);
    
    const trianglePointX = shoulderMidX;
    const trianglePointY = shoulderMidY - (shoulderWidth * 0.15);
    
    // Check hip visibility
    const leftHipVisible = landmarks.leftHip.visibility >= 0.5;
    const rightHipVisible = landmarks.rightHip.visibility >= 0.5;
    const bothHipsVisible = leftHipVisible && rightHipVisible;
    const oneHipVisible = leftHipVisible !== rightHipVisible;
    
    // Calculate hip positions (same logic as generation)
    let leftHipX: number, leftHipY: number, rightHipX: number, rightHipY: number;
    
    if (bothHipsVisible) {
      leftHipX = landmarks.leftHip.x;
      leftHipY = landmarks.leftHip.y;
      rightHipX = landmarks.rightHip.x;
      rightHipY = landmarks.rightHip.y;
    } else if (oneHipVisible) {
      if (leftHipVisible) {
        leftHipX = landmarks.leftHip.x;
        leftHipY = landmarks.leftHip.y;
        const hipOffset = leftHipX - shoulderMidX;
        rightHipX = shoulderMidX - hipOffset;
        rightHipY = leftHipY;
      } else {
        rightHipX = landmarks.rightHip.x;
        rightHipY = landmarks.rightHip.y;
        const hipOffset = rightHipX - shoulderMidX;
        leftHipX = shoulderMidX - hipOffset;
        leftHipY = rightHipY;
      }
    } else {
      const assumedHipWidth = shoulderWidth * 0.7;
      const assumedTorsoLength = shoulderWidth * 1.5;
      const assumedHipY = shoulderMidY + assumedTorsoLength;
      leftHipX = shoulderMidX - (assumedHipWidth / 2);
      leftHipY = assumedHipY;
      rightHipX = shoulderMidX + (assumedHipWidth / 2);
      rightHipY = assumedHipY;
    }
    
    // Calculate bounding box for position updates
    const minX = Math.min(trianglePointX, leftShoulder.x, leftHipX, rightHipX, rightShoulder.x);
    const maxX = Math.max(trianglePointX, leftShoulder.x, leftHipX, rightHipX, rightShoulder.x);
    const minY = Math.min(trianglePointY, leftShoulder.y, leftHipY, rightHipY, rightShoulder.y);
    const maxY = Math.max(trianglePointY, leftShoulder.y, leftHipY, rightHipY, rightShoulder.y);
    
    const polygonWidth = maxX - minX;
    const polygonHeight = maxY - minY;
    
    for (const circle of circles) {
      // Update age
      circle.age += deltaTime;
      
      // Update max size based on motion intensity
      // Formula: maxSize = baseSize * (1 + 2 * intensity)
      const motionResponsiveMaxSize = this.baseCircleSize * (1 + 2 * torsoMotionIntensity);
      
      // Smooth max size changes
      const maxSizeSmoothingFactor = 0.1;
      circle.maxSize = circle.maxSize + (motionResponsiveMaxSize - circle.maxSize) * maxSizeSmoothingFactor;
      
      // Update position to follow torso movement using stored relative position
      if (circle.relativePosition) {
        // Use the stored relative position within bounding box
        circle.position = {
          x: minX + circle.relativePosition.x * polygonWidth,
          y: minY + circle.relativePosition.y * polygonHeight
        };
      }
      
      // Handle lifecycle (same as limb circles)
      if (circle.age < 0) {
        circle.currentSize = circle.maxSize * this.startSizeFactor;
      } else if (circle.age < circle.growthDuration) {
        const growthProgress = circle.age / circle.growthDuration;
        const startSize = circle.maxSize * this.startSizeFactor;
        circle.currentSize = startSize + (circle.maxSize - startSize) * growthProgress;
      } else {
        // Rebirth
        circle.age = 0;
        circle.currentSize = circle.maxSize * this.startSizeFactor;
        
        circle.growthDuration = this.minGrowthDuration + 
          Math.random() * (this.maxGrowthDuration - this.minGrowthDuration);
        
        const baseGrowthRate = circle.maxSize / circle.growthDuration;
        const growthRateVariation = (Math.random() - 0.5) * 2 * this.growthRateVariation;
        circle.growthRate = baseGrowthRate * (1 + growthRateVariation);
      }
    }
  }
  
  /**
   * Get motion intensity for a specific limb.
   * Returns 0 if limb motion data is not available.
   */
  private getLimbMotionIntensity(limbName: string, motionData: MotionData): number {
    const limbMotionMap: Record<string, keyof MotionData> = {
      'leftUpperArm': 'leftUpperArm',
      'leftForearm': 'leftForearm',
      'rightUpperArm': 'rightUpperArm',
      'rightForearm': 'rightForearm',
      'leftThigh': 'leftThigh',
      'leftShin': 'leftShin',
      'rightThigh': 'rightThigh',
      'rightShin': 'rightShin'
    };
    
    const motionKey = limbMotionMap[limbName];
    if (!motionKey) {
      return 0;
    }
    
    return motionData[motionKey]?.intensity ?? 0;
  }
  
  /**
   * Render circles to the canvas.
   * Draws filled circles with transparency using the primary color from visual config.
   * 
   * Validates Requirements 3.5.1.12
   */
  private renderCircles(p: p5, circles: Circle[], config: VisualConfig): void {
    // Set drawing style
    const color = config.primaryColor;
    p.fill(color.r, color.g, color.b, color.a * this.circleAlpha * 255);
    p.noStroke();
    
    // Render circles in order (back to front)
    for (const circle of circles) {
      // Skip circles that haven't started yet
      if (circle.age < 0) {
        continue;
      }
      
      // Convert normalized position to screen coordinates
      const screenX = circle.position.x * p.width;
      const screenY = circle.position.y * p.height;
      
      // Draw filled circle
      p.circle(screenX, screenY, circle.currentSize);
    }
  }
}
